import React, { useState, useMemo, useCallback } from 'react';
import {
  TextField,
  Box,
  Table,
  TableBody,
  TableContainer as MuiTableContainer,
  TableHead,
  Paper,
  InputAdornment,
  Select,
  SelectChangeEvent,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TableMenuButton from 'components/button/TableMenuButton';
import UserSwitch from 'components/switch/UserSwitch';
import { useDeleteUserMutation, useGetFirmsQuery, useGetUserQuery, useUpdateRoleMutation } from '../../api/api';
import FigmaModal from 'components/modal/FigmaModal';
import SettingsIcon from '@mui/icons-material/Settings';
import { StyledTableCell, StyledTableRow, TableContainer, TableLoadingRow } from 'components/tables/styledTableComponents';
import EditUserModal from 'pages/UserPages/EditUserModal';
import { useLocation } from 'react-router-dom';
import ErrorModal from 'components/modal/ErrorModal';
import { getErrorMessage } from 'lib/utils/getErrorMessage';

const downArrowPath = '/assets/icons/ArrowsDown.svg';
const upArrowPath = '/assets/icons/ArrowsUp.svg';
const upDownArrowPath = '/assets/icons/ArrowsDownUp.svg';

interface UserTableProps {
  users: any[];
  usersLoading: boolean;
  role: 'Firm User' | 'Firm Admin' | 'Sub Super Admin' | 'Super Admin';
}

// Checks if there are any other admins in the firm
export const isOtherFirmAdmins = (userId, allUsers) => {
  const userToBeChanged = allUsers.find((user) => user.auth0_id === userId);
  const adminsInUsersFirm = allUsers.filter((user) => (['Firm Admin', 'Sub Super Admin', 'Super Admin'].includes(user.site_role) && userToBeChanged.firm === user.firm && userToBeChanged.auth0_id !== user.auth0_id));
  return adminsInUsersFirm.length > 0
}

// Roles organised from least priveliged to most priveliged
const roleHierarchy = ['Firm User', 'Firm Admin', 'Sub Super Admin', 'Super Admin'];

const UserTable: React.FC<UserTableProps> = ({ users, usersLoading, role }) => {
  const location = useLocation();
  const [firmFilter, setFirmFilter] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('firm') || 'All Firms';
  });
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { data: userData, isLoading: userDataIsLoading } = useGetUserQuery();
  const { data: firms } = useGetFirmsQuery();

  const [updateRole, { isLoading: roleUpdatePending, error: updateError, reset: resetUpdate }] = useUpdateRoleMutation();
  const [deleteUser, { error: deleteError, reset: resetDelete }] = useDeleteUserMutation();

  const error = deleteError || updateError;
  const handleClose = () => {
    resetDelete();
    resetUpdate();
  };

  const canUserEditAndDelete = useMemo(() => {
    const userRoleRank = roleHierarchy.indexOf(userData?.site_role ?? "");
    const tableRoleRank = roleHierarchy.indexOf(role);
    return userRoleRank > tableRoleRank;
  }, [userData?.site_role, role]);

  const displayFirmColumn = role !== 'Sub Super Admin'

  const handleChangeFirmFilter = (event: SelectChangeEvent) => {
    setFirmFilter(event.target.value as string);
  };

  const handleChangeRole = (event: SelectChangeEvent) => {
    const newRole = event.target.value;
    if (isOtherFirmAdmins(event.target.name, users) || newRole !== 'Firm User') {
      updateRole({ auth0_id: event.target.name, site_role: event.target.value });
    } else {
      setIsModalOpen(true);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
  };

  const handleCloseEditModal = () => {
    setSelectedUser(null);
  };

  const handleSortTable = (key: string) => {
    if (key === 'site_role') {
      return;
    }

    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: string) => {
    const arrowUp = <img src={upArrowPath} alt="Sort Ascending" />;
    const arrowDown = <img src={downArrowPath} alt="Sort Descending" />;
    const arrowUpDown = <img src={upDownArrowPath} alt="Sort" />;

    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? arrowUp : arrowDown;
    }
    return arrowUpDown;
  };

  const sortData = useCallback((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue);
      return sortConfig.direction === 'ascending' ? comparison : -comparison;
    }

    if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;

    return a.auth0_id.localeCompare(b.auth0_id);
  }, [sortConfig]);

  const filteredData = useMemo(() => {
    let filteredItems = [...users];

    filteredItems = filteredItems.filter((user) => user.site_role === role);

    if (firmFilter !== 'All Firms') {
      filteredItems = filteredItems.filter((user) => user.firm === firmFilter);
    }

    if (searchTerm) {
      filteredItems = filteredItems.filter((user) => {
        return (
          (user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
          (user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.address?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
          (user.city?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
          (user.country?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
          (user.firm?.toLowerCase().includes(searchTerm.toLowerCase()) || '')
        );
      });
    }

    return filteredItems.sort(sortData);
  }, [users, sortConfig, searchTerm, firmFilter]);

  const tableHeaderKeys = {
    first_name: 'Name',
    email: 'Email',
    ...(displayFirmColumn && { firm: 'Firm' }),
    address: 'Address',
    city: 'City',
    subnational: 'State / Province / Region',
    country: 'Country',
    is_enabled: 'Status',
    site_role: 'Role',
  }

  return (
    <TableContainer>
      <FigmaModal open={isModalOpen} onClose={() => setIsModalOpen(false)} type={'finalFirmAdminWarning'} />
      <ErrorModal
        open={!!error}
        onClose={handleClose}
        errorMessage={error ? getErrorMessage(error) : null}
      />
      <EditUserModal
        open={selectedUser !== null}
        onClose={handleCloseEditModal}
        user={selectedUser}
        roleName={role}
      />
      <Box sx={{ mt: '10px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ width: '100%', height: '100%', color: '#555555', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700', wordWrap: 'break-word', marginBottom: '10px' }}>
              Search
            </div>
            <TextField
              placeholder="Search ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                style: {
                  borderRadius: 22,
                  height: '44px',
                  width: '343px',
                  marginRight: '16px',
                },
              }}
            />
          </Box>
          {userData?.site_role !== 'Firm Admin' && userData?.site_role !== 'Firm User' ?
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ width: '100%', height: '100%', color: '#555555', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700', wordWrap: 'break-word', marginBottom: '10px' }}>
                Filter by Firms
              </div>
              <FormControl>
                <InputLabel id="firm-filter-select-label">Filter by Firms</InputLabel>
                <Select
                  sx={{
                    borderRadius: 22,
                    height: '44px',
                    width: '220px',
                  }}
                  label="firm-filter-select"
                  labelId="firm-filter-select-label"
                  value={firmFilter}
                  onChange={handleChangeFirmFilter}
                >
                  <MenuItem key="no-filter" id="no-filter" value="All Firms">All Firms</MenuItem>
                  {firms?.map((firm, index) => (
                    <MenuItem key={index} value={firm.name}>{firm.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box> : null}
        </Box>
        <MuiTableContainer component={Paper} sx={{ mt: '20px', overflow: 'clip' }}>
          <div style={{ width: '100%', height: '4px', background: 'linear-gradient(90deg, #059BD6 0%, #9FD64D 100%)' }} />
          <Table>
            <TableHead>
              <StyledTableRow>
                {Object.entries(tableHeaderKeys).map(([key, title]) => (
                  <StyledTableCell key={key} onClick={() => handleSortTable(key)} className={sortConfig.key === key ? 'sorted' : ''}>
                    {title.charAt(0).toUpperCase() + title.slice(1).replace(/([A-Z])/g, ' $1')}
                    {key !== 'site_role' &&
                      <span className="sort-icon">{getSortIcon(key)}</span>
                    }
                  </StyledTableCell>
                ))}
                <StyledTableCell sx={{
                  position: 'sticky !important',
                  right: 0,
                  zIndex: 100,
                }}>
                  <SettingsIcon sx={{
                    display: 'flex',
                    alignSelf: 'center'
                  }} />
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {usersLoading ?
                <TableLoadingRow colSpan={Object.keys(tableHeaderKeys).length + 1} />
                :
                filteredData?.map((user, index) => (
                  <StyledTableRow key={user.auth0_id}>
                    <StyledTableCell>{user.first_name} {user.last_name}</StyledTableCell>
                    <StyledTableCell>{user.email}</StyledTableCell>
                    {displayFirmColumn && <StyledTableCell>{user.firm}</StyledTableCell>}
                    <StyledTableCell>{user.address}</StyledTableCell>
                    <StyledTableCell>{user.city}</StyledTableCell>
                    <StyledTableCell>{user.subnational}</StyledTableCell>
                    <StyledTableCell>{user.country}</StyledTableCell>
                    <StyledTableCell>
                      <FormGroup>
                        <UserSwitch rowData={user} userRole={userData?.site_role} users={users} />
                      </FormGroup>
                    </StyledTableCell>
                    <StyledTableCell>
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">Firm Role</InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={user.site_role ?? 'Firm Role'}
                          name={user.auth0_id}
                          label="Firm Role"
                          onChange={handleChangeRole}
                          disabled={roleUpdatePending || userDataIsLoading || userData?.site_role === 'Firm User'}
                          sx={{
                            minWidth: '163px',
                          }}
                        >
                          <MenuItem value={'Firm User'}>Firm User</MenuItem>
                          <MenuItem disabled={!['Super Admin', 'Sub Super Admin', 'Firm Admin'].includes(userData?.site_role ?? '')} value={'Firm Admin'}>Firm Admin</MenuItem>
                          <MenuItem disabled={userData?.site_role !== 'Super Admin'} value={'Sub Super Admin'}>Sub Super Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </StyledTableCell>
                    <StyledTableCell style={{ overflow: 'visible', position: 'sticky', right: 0, padding: 0 }}>
                      <TableMenuButton
                        tableContext="users"
                        rowData={user}
                        index={index}
                        disableDelete={!canUserEditAndDelete}
                        disableEdit={!canUserEditAndDelete}
                        deleteMutation={deleteUser}
                        onEdit={() => handleEditUser(user)}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </MuiTableContainer>
      </Box>
    </TableContainer>
  );
};

export default UserTable;